import { useState, useMemo, useEffect, useRef } from "react";
import { useApolloClient, type DocumentNode } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Plus, List, Loader2, User as UserIcon, Users } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { isLovablePreview } from "@/lib/utils/environment";
import { useWorkspaceId } from "@/lib/hooks/use-workspace-id";
import { useCurrentUser } from "@/lib/hooks/use-current-user";
import { useListFolders, type ListFolderCategory } from "@/lib/hooks/use-list-folders";
import { computeMembershipDiff } from "@/lib/utils/list-membership-diff";
import { pollUntil } from "@/lib/utils/poll-until";
import {
  useGetUserListsQuery,
  useGetRecordListMembershipsQuery,
  useCreateListMutation,
  useAddRecordsToListMutation,
  useRemoveRecordsFromListMutation,
  GetListAccountCountDocument,
  GetListContactCountDocument,
  GetListDealCountDocument,
  ListModelType,
  type GetListAccountCountQuery,
  type GetListContactCountQuery,
  type GetListDealCountQuery,
} from "@/lib/graphql/types-and-hooks";


const entityToModelType: Record<string, ListModelType> = {
  contact: ListModelType.Contact,
  people: ListModelType.Contact,
  company: ListModelType.Account,
  deal: ListModelType.Deal,
};

const entityToRoute: Record<string, string> = {
  contact: "/people",
  people: "/people",
  company: "/companies",
  deal: "/deals",
};

const entityToRefetchQuery: Record<string, string> = {
  contact: 'getContacts',
  people: 'getContacts',
  company: 'getAccounts',
  deal: 'getDeals',
};

const entityToFolderCategory: Record<string, ListFolderCategory> = {
  contact: "people",
  people: "people",
  company: "companies",
  deal: "deals",
};

// Grid-shaped count probes per entity. List membership is written to Postgres
// synchronously, but the destination grids filter via OpenSearch — so before
// navigating to a just-populated list we poll the same count the grid will
// query, otherwise the user can land on an empty list while indexing lags.
const entityToCountProbe: Record<string, { query: DocumentNode; getCount: (data: unknown) => number | null }> = {
  contact: {
    query: GetListContactCountDocument,
    getCount: data => (data as GetListContactCountQuery)?.contacts?.totalCount ?? null,
  },
  people: {
    query: GetListContactCountDocument,
    getCount: data => (data as GetListContactCountQuery)?.contacts?.totalCount ?? null,
  },
  company: {
    query: GetListAccountCountDocument,
    getCount: data => (data as GetListAccountCountQuery)?.accounts?.totalCount ?? null,
  },
  deal: {
    query: GetListDealCountDocument,
    getCount: data => (data as GetListDealCountQuery)?.deals?.totalCount ?? null,
  },
};

// Append " (2)", " (3)", … until the candidate doesn't collide with an
// existing list name (case-insensitive).
function dedupeListName(candidate: string, existingNames: Iterable<string>): string {
  const names = new Set([...existingNames].map(n => n.toLowerCase()));
  if (!names.has(candidate.toLowerCase())) return candidate;
  let suffix = 2;
  while (names.has(`${candidate} (${suffix})`.toLowerCase())) suffix++;
  return `${candidate} (${suffix})`;
}

/**
 * Catalog key per `entityLabel` prop value. The prop is a wire-ish identifier
 * ("contact", "company", "deal", "people", the "record" default); only the word
 * the user reads is translated, and i18next picks the singular/plural form.
 * Written as full literals so the locale audit's usage scan can see them.
 */
const ENTITY_KEYS: Record<string, string> = {
  record: "lists.entity.record",
  contact: "lists.entity.contact",
  people: "lists.entity.people",
  company: "lists.entity.company",
  deal: "lists.entity.deal",
};

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** Tri-state checkbox values. Identifiers, never rendered. */
type CheckState = "checked" | "unchecked" | "indeterminate";
const UNCHECKED: CheckState = "unchecked";

/** "added to 2 lists and removed from 1 list" — the conjunction is per-language. */
function joinChanges(parts: string[], t: Translate): string {
  if (parts.length < 2) return parts[0] ?? "";
  return t("lists.addTo.changeJoin", { first: parts[0], second: parts[1] });
}

function entityWord(label: string, count: number, t: Translate): string {
  const key = ENTITY_KEYS[label];
  return key ? t(key, { count }) : label;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  count: number;
  entityLabel?: string;
  /** @deprecated Use recordIds + backend integration instead */
  onConfirm?: (listName: string) => void;
  /** Preview-mode callback to remove selected records from a list by name. */
  onRemove?: (listName: string) => void;
  existingLists?: string[];
  /**
   * PREVIEW ONLY: map of list name → recordIds already in that list. When
   * provided in preview, the dialog switches to multi-select mode (show + add +
   * remove). In production this prop is ignored — the dialog self-fetches real
   * membership via getRecordListMemberships and multi-selects by list id.
   */
  recordMemberships?: Record<string, string[]>;
  recordIds?: string[];
  onDone?: () => void;
}

export default function AddToListDialog({ open, onOpenChange, count, entityLabel = "record", onConfirm, onRemove, existingLists, recordMemberships, recordIds = [], onDone }: Props) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const apolloClient = useApolloClient();
  const preview = isLovablePreview();
  const workspaceId = useWorkspaceId();
  const { currentUser } = useCurrentUser();
  const modelType = entityToModelType[entityLabel] || ListModelType.Contact;
  const route = entityToRoute[entityLabel] || "/people";
  const folderCategory = entityToFolderCategory[entityLabel] || "people";
  const { setListScope } = useListFolders(folderCategory);

  const { data: listsData, loading: loadingLists } = useGetUserListsQuery({
    variables: { filter: { workspaceId: workspaceId || undefined, modelType, userId: currentUser?.id || undefined } },
    skip: preview || !workspaceId || !open,
  });

  const [createListMutation] = useCreateListMutation();
  const [addRecordsToListMutation] = useAddRecordsToListMutation();
  const [removeRecordsFromListMutation] = useRemoveRecordsFromListMutation();
  const [saving, setSaving] = useState(false);

  // Flipped on unmount so an in-flight post-save poll can't toast or navigate
  // after the user has moved to another page.
  const cancelRef = useRef({ cancelled: false });
  useEffect(() => {
    const signal = cancelRef.current;
    signal.cancelled = false;
    return () => { signal.cancelled = true; };
  }, []);

  const backendLists = useMemo(() => {
    if (!listsData?.userLists?.edges) return [];
    return listsData.userLists.edges
      .map(edge => edge.node?.list)
      .filter((list): list is NonNullable<typeof list> => list !== null && list !== undefined)
      .filter(list => list.static)
      .map(list => ({ id: list.id, name: list.name || t("lists.addTo.unnamed") }));
  }, [listsData]);

  const dummyLists = useMemo(
    () => [...(existingLists || [])].sort((a, b) => a.localeCompare(b)),
    [existingLists],
  );

  // Unified list rows for both modes: preview uses list names as ids, production
  // uses backend list ids. All multi-select state below is keyed by `id`.
  const sortedBackendLists = useMemo(
    () => [...backendLists].sort((a, b) => a.name.localeCompare(b.name)),
    [backendLists],
  );
  const displayLists = useMemo(
    () => (preview ? dummyLists.map(name => ({ id: name, name })) : sortedBackendLists),
    [preview, dummyLists, sortedBackendLists],
  );

  const [selected, setSelected] = useState<string | null>(null);
  const [creatingNew, setCreatingNew] = useState(false);
  const [newName, setNewName] = useState("");
  const [newIsPublic, setNewIsPublic] = useState(true);
  // Multi-select tri-state, keyed by list id (in preview the list name is the id).
  const [checkedLists, setCheckedLists] = useState<Record<string, CheckState>>({});

  // Production: batch-fetch which lists the selected records already belong to
  // in a single round-trip. Preview uses the `recordMemberships` prop instead.
  const { data: membershipData, loading: membershipLoading } = useGetRecordListMembershipsQuery({
    variables: { workspaceId: workspaceId || "", modelType, recordIds },
    skip: preview || !workspaceId || !open || recordIds.length === 0,
    fetchPolicy: "cache-and-network",
  });

  // Multi-select mode: preview when the caller passes membership; production
  // once the membership query has returned for the current selection.
  const multiSelect =
    (preview && !!recordMemberships) ||
    (!preview && !!workspaceId && recordIds.length > 0 && !!membershipData);

  // While membership is in flight the dialog would render single-select and
  // then morph to multi-select, dropping any click made in between — keep the
  // list behind the loading spinner until membership is known. If the query
  // errors (e.g. backend not deployed yet), loading ends and the dialog falls
  // back to single-select add as designed.
  const membershipPending =
    !preview && !!workspaceId && recordIds.length > 0 && !membershipData && membershipLoading;

  // Initial membership per list id: "all" = every selected record is in it,
  // "some" = partial, "none" = none.
  const initialMembership = useMemo(() => {
    const out: Record<string, "all" | "some" | "none"> = {};
    if (recordIds.length === 0) return out;
    if (preview) {
      if (!recordMemberships) return out;
      for (const name of dummyLists) {
        const members = new Set(recordMemberships[name] || []);
        const hits = recordIds.filter(id => members.has(id)).length;
        out[name] = hits === 0 ? "none" : hits === recordIds.length ? "all" : "some";
      }
      return out;
    }
    const counts: Record<string, number> = {};
    for (const rec of membershipData?.recordListMemberships ?? []) {
      for (const lid of rec.listIds) counts[lid] = (counts[lid] || 0) + 1;
    }
    for (const list of backendLists) {
      const hits = counts[list.id] || 0;
      out[list.id] = hits === 0 ? "none" : hits === recordIds.length ? "all" : "some";
    }
    return out;
    // Keyed by primitives so this only recomputes on meaningful changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview, recordMemberships, recordIds.join(","), dummyLists.join(","), membershipData, backendLists]);

  // Seed checked state when the dialog opens, or when multi-select turns on
  // after the production membership query resolves.
  useEffect(() => {
    if (!open || !multiSelect) return;
    const seed: Record<string, CheckState> = {};
    for (const list of displayLists) {
      const state = initialMembership[list.id];
      seed[list.id] = state === "all" ? "checked" : state === "some" ? "indeterminate" : "unchecked";
    }
    setCheckedLists(seed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, multiSelect]);

  // Derived below, but compute here for the auto-create effect.
  const hasNoLists = !loadingLists && (preview
    ? dummyLists.length === 0
    : (backendLists.length === 0));

  // Auto-enter "create new list" mode when the dialog opens and there are no
  // existing lists — skips the empty state entirely.
  useEffect(() => {
    if (open && hasNoLists && !creatingNew && !selected) {
      setCreatingNew(true);
    }
  }, [open, hasNoLists, creatingNew, selected]);

  const reset = () => {
    setSelected(null);
    setCreatingNew(false);
    setNewName("");
    setNewIsPublic(true);
    setCheckedLists({});
  };

  // Diff of the desired checkbox state vs current membership — drives both the
  // confirm button state and the confirm handler.
  const multiDiff = useMemo(
    () => computeMembershipDiff(displayLists.map(l => l.id), initialMembership, checkedLists),
    [displayLists, initialMembership, checkedLists],
  );

  // Shared post-mutation sequence for both production paths: close the dialog
  // before any refetch can repaint it, refetch the affected queries once, then
  // (when navigating to a list) hold a loading toast until the OpenSearch-backed
  // grid count shows the records — replaces the old blind 1.5s sleep, which
  // lost the race against indexing and landed users on an empty list.
  const finalizeSave = async (
    successText: string,
    navigateTo: { listId: string; expectedCount: number } | null,
  ) => {
    reset();
    onOpenChange(false);
    onDone?.();
    apolloClient.refetchQueries({
      include: [entityToRefetchQuery[entityLabel] || 'getContacts', 'getUserLists', 'getRecordListMemberships'],
    }).catch(() => { /* refetch failures surface via the queries themselves */ });

    if (!navigateTo) {
      toast.success(successText);
      return;
    }

    const probe = entityToCountProbe[entityLabel] || entityToCountProbe.contact;
    const toastId = toast.loading(t("lists.addTo.openingList", { text: successText }));
    const result = await pollUntil(
      () => apolloClient.query({
        query: probe.query,
        variables: { filter: { listMembership: navigateTo.listId } },
        fetchPolicy: "no-cache",
      }).then(res => probe.getCount(res.data)),
      n => n !== null && n >= navigateTo.expectedCount,
      { intervalMs: 1000, timeoutMs: 10000, signal: cancelRef.current },
    );
    if (result.reason === "cancelled") {
      toast.dismiss(toastId);
      return;
    }
    toast.success(
      result.ok ? successText : t("lists.addTo.syncing", { text: successText }),
      { id: toastId },
    );
    navigate(`${route}?listId=${encodeURIComponent(navigateTo.listId)}`);
  };

  const handleConfirm = async () => {
    // Production multi-select: diff checked state against current membership and
    // apply add/remove mutations per changed list (by list id).
    if (multiSelect && !preview && workspaceId) {
      const toAdd = [...multiDiff.toAdd];
      const { toRemove } = multiDiff;
      const wantCreate = creatingNew && !!newName.trim();
      if (toAdd.length === 0 && toRemove.length === 0 && !wantCreate) {
        reset(); onOpenChange(false); return;
      }

      setSaving(true);
      let createdListId: string | null = null;
      try {
        if (wantCreate) {
          const candidate = dedupeListName(newName.trim(), sortedBackendLists.map(l => l.name));
          const createResult = await createListMutation({
            variables: {
              input: { workspaceId, ownerId: currentUser?.id || '', name: candidate, modelType, public: newIsPublic, static: true },
            },
          });
          createdListId = createResult.data?.createList?.list?.id || null;
          if (!createdListId) throw new Error(t("lists.addTo.createFailed"));
          toAdd.push(createdListId);
        }

        // The changed lists are independent — run the mutations concurrently;
        // the affected queries are refetched once in finalizeSave, after the
        // dialog has closed (a mid-save getUserLists refetch used to pop the
        // just-created list into the open dialog unchecked).
        await Promise.all([
          ...toAdd.map(listId =>
            addRecordsToListMutation({
              variables: { input: { workspaceId, listId, recordIds } },
            }).then(res => {
              if (!res.data?.addRecordsToList?.ok) throw new Error(t("lists.addTo.addFailed"));
            })),
          ...toRemove.map(listId =>
            removeRecordsFromListMutation({
              variables: { input: { workspaceId, listId, recordIds } },
            }).then(res => {
              if (!res.data?.removeRecordsFromList?.ok) throw new Error(t("lists.addTo.removeFailed"));
            })),
        ]);
      } catch (error) {
        console.error("Failed to update lists:", error);
        toast.error(error instanceof Error ? error.message : t("lists.addTo.updateFailed"));
        // Keep the dialog open so the change can be retried. If the list was
        // already created before the failure, drop out of create mode so a
        // retry can't create a duplicate — refetch surfaces the new list.
        if (createdListId) {
          setCreatingNew(false);
          setNewName("");
          apolloClient.refetchQueries({ include: ['getUserLists'] }).catch(() => {});
        }
        setSaving(false);
        return;
      }
      setSaving(false);

      const word = entityWord(entityLabel, count, t);
      const parts: string[] = [];
      if (toAdd.length) parts.push(t("lists.addTo.addedTo", { count: toAdd.length }));
      if (toRemove.length) parts.push(t("lists.addTo.removedFrom", { count: toRemove.length }));
      await finalizeSave(
        t("lists.addTo.changeSummary", { count, entity: word, changes: joinChanges(parts, t) }),
        createdListId ? { listId: createdListId, expectedCount: recordIds.length } : null,
      );
      return;
    }

    // Multi-select preview flow: diff checked state against initial memberships
    // (in preview the diff ids are the list names).
    if (multiSelect) {
      const added = [...multiDiff.toAdd];
      const removed = multiDiff.toRemove;
      // New-list creation still adds all selected records to a fresh list.
      let createdName: string | null = null;
      if (creatingNew && newName.trim()) {
        const candidate = dedupeListName(newName.trim(), dummyLists);
        createdName = candidate;
        added.push(candidate);
      }
      if (added.length === 0 && removed.length === 0) {
        reset(); onOpenChange(false); return;
      }
      added.forEach(n => onConfirm?.(n));
      removed.forEach(n => onRemove?.(n));
      if (createdName) {
        setListScope(createdName, newIsPublic ? "shared" : "personal");
      }
      const parts: string[] = [];
      if (added.length) parts.push(t("lists.addTo.addedTo", { count: added.length }));
      if (removed.length) parts.push(t("lists.addTo.removedFrom", { count: removed.length }));
      toast.success(t("lists.addTo.changeSummary", {
        count,
        entity: entityWord(entityLabel, count, t),
        changes: joinChanges(parts, t),
      }));
      onDone?.();
      reset();
      onOpenChange(false);
      if (createdName) navigate(`${route}?list=${encodeURIComponent(createdName)}`);
      return;
    }

    const isCreating = creatingNew && newName.trim();
    let selectedListName = isCreating ? newName.trim() : null;
    const selectedListId = !isCreating ? selected : null;

    if (!selectedListId && !selectedListName) return;

    // Auto-deduplicate list name by appending (2), (3), etc.
    if (selectedListName) {
      selectedListName = dedupeListName(selectedListName, displayLists.map(l => l.name));
    }

    // Preview mode: use legacy callback
    if (preview || !workspaceId || recordIds.length === 0) {
      const listName = selectedListName || backendLists.find(l => l.id === selectedListId)?.name || selected || '';
      onConfirm?.(listName);
      // Apply folder assignment (client-side) when creating a new list.
      if (isCreating && listName) {
        setListScope(listName, newIsPublic ? "shared" : "personal");
      }
      reset();
      onOpenChange(false);
      navigate(`${route}?list=${encodeURIComponent(listName)}`);
      return;
    }

    setSaving(true);
    const isNewList = Boolean(isCreating && selectedListName);
    let listId = selectedListId;
    try {
      // Create new list if needed
      if (isCreating && selectedListName) {
        const createResult = await createListMutation({
          variables: {
            input: {
              workspaceId: workspaceId,
              ownerId: currentUser?.id || '',
              name: selectedListName,
              modelType,
              public: newIsPublic,
              static: true,
            },
          },
        });
        listId = createResult.data?.createList?.list?.id || null;
        if (!listId) throw new Error(t("lists.addTo.createFailed"));
      }

      // Add records to list
      if (listId) {
        const res = await addRecordsToListMutation({
          variables: {
            input: {
              workspaceId: workspaceId,
              listId,
              recordIds,
            },
          },
        });
        if (!res.data?.addRecordsToList?.ok) {
          throw new Error(t("lists.addTo.unsuccessful"));
        }
      }
    } catch (error) {
      console.error("Failed to add to list:", error);
      toast.error(error instanceof Error ? error.message : t("lists.addTo.addFailed"));
      // Keep the dialog open for retry; if the list was created before the add
      // failed, select it so a retry can't create a duplicate.
      if (isNewList && listId) {
        setCreatingNew(false);
        setNewName("");
        setSelected(listId);
        apolloClient.refetchQueries({ include: ['getUserLists'] }).catch(() => {});
      }
      setSaving(false);
      return;
    }
    setSaving(false);

    if (!listId) {
      reset();
      onOpenChange(false);
      return;
    }
    const listName = selectedListName || backendLists.find(l => l.id === listId)?.name || '';
    await finalizeSave(
      t("lists.addTo.addedToNamed", { count, entity: entityWord(entityLabel, count, t), name: listName }),
      // For a brand-new list the exact expected count is known; a pre-existing
      // list may already have rows, so any hit proves it's queryable.
      { listId, expectedCount: isNewList ? recordIds.length : 1 },
    );
  };

  const handleCancel = () => {
    reset();
    onOpenChange(false);
  };

  const hasMultiChanges = multiSelect && (multiDiff.toAdd.length > 0 || multiDiff.toRemove.length > 0);
  const canConfirm = (multiSelect
    ? (hasMultiChanges || (creatingNew && newName.trim()))
    : (selected || (creatingNew && newName.trim()))) && !saving;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleCancel(); else onOpenChange(v); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("lists.addTo.title", { count, entity: entityWord(entityLabel, count, t) })}</DialogTitle>
        </DialogHeader>

        {((loadingLists || membershipPending) && !preview) || displayLists.length > 0 ? (
        <div className="space-y-1 max-h-[200px] overflow-y-auto">
          {(loadingLists || membershipPending) && !preview ? (
            <div className="flex items-center gap-2 py-4 justify-center text-muted-foreground text-xs">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t("lists.addTo.loading")}
            </div>
          ) : displayLists.length === 0 ? null : (
            displayLists.map(list => multiSelect ? (
              (() => {
                const state = initialMembership[list.id];
                const current = checkedLists[list.id] ?? UNCHECKED;
                return (
                  <label
                    key={list.id}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm hover:bg-muted cursor-pointer"
                  >
                    <Checkbox
                      checked={current === "indeterminate" ? "indeterminate" : current === "checked"}
                      onCheckedChange={(v) => setCheckedLists(prev => ({ ...prev, [list.id]: v === true ? "checked" : "unchecked" }))}
                    />
                    <List className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="flex-1 truncate">{list.name}</span>
                    {state === "all" && <span className="text-[10px] text-muted-foreground shrink-0">{t("lists.addTo.inList")}</span>}
                    {state === "some" && <span className="text-[10px] text-muted-foreground shrink-0">{t("lists.addTo.partial")}</span>}
                  </label>
                );
              })()
            ) : (
              <button
                key={list.id}
                onClick={() => { setSelected(list.id); setCreatingNew(false); }}
                className={cn(
                  "w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-left transition",
                  selected === list.id && !creatingNew
                    ? "bg-accent text-accent-foreground"
                    : "hover:bg-muted"
                )}
              >
                <List className="h-4 w-4 shrink-0" />
                {list.name}
              </button>
            ))
          )}
        </div>
        ) : null}

        <div className={cn(displayLists.length > 0 && "border-t border-border pt-3")}>
          {!creatingNew ? (
            <button
              onClick={() => { setCreatingNew(true); setSelected(null); }}
              className="flex items-center gap-2 text-sm text-accent hover:underline"
            >
              <Plus className="h-4 w-4" /> {t("lists.addTo.createNew")}
            </button>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <input
                  autoFocus
                  value={newName}
                  disabled={saving}
                  onChange={e => setNewName(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter" && newName.trim() && !saving) handleConfirm(); }}
                  placeholder={t("lists.addTo.namePlaceholder")}
                  className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/20 disabled:opacity-50"
                />
                <button
                  disabled={saving}
                  onClick={() => { setCreatingNew(false); setNewName(""); }}
                  className="text-xs text-muted-foreground hover:text-foreground disabled:opacity-50"
                >
                  {t("common.cancel")}
                </button>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <span className="text-muted-foreground">{t("lists.addTo.visibility")}</span>
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => setNewIsPublic(true)}
                  className={cn(
                    "flex items-center gap-1.5 px-2 py-1 rounded border transition disabled:opacity-50",
                    newIsPublic ? "border-accent text-accent bg-accent/10" : "border-border hover:bg-muted"
                  )}
                >
                  <Users className="h-3.5 w-3.5" /> {t("lists.addTo.shared")}
                </button>
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => setNewIsPublic(false)}
                  className={cn(
                    "flex items-center gap-1.5 px-2 py-1 rounded border transition disabled:opacity-50",
                    !newIsPublic ? "border-accent text-accent bg-accent/10" : "border-border hover:bg-muted"
                  )}
                >
                  <UserIcon className="h-3.5 w-3.5" /> {t("lists.addTo.personal")}
                </button>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel} disabled={saving}>{t("common.cancel")}</Button>
          <Button onClick={handleConfirm} disabled={!canConfirm}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {creatingNew && newName.trim() ? t("lists.addTo.createAndAdd") : multiSelect ? t("lists.addTo.saveChanges") : t("lists.addTo.addToList")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
